import React, { useEffect, useState } from 'react';
import heroImage from '../assets/hero-image.jpg';
import logo from '../assets/logo.png';
import slide1 from '../assets/slide1.png';
import recipe1 from '../assets/recipe1.png';
import ctaBg from '../assets/cta-bg.jpg';
import { scrollToTop } from '../utils/scroll';
import { FaqAccordion } from '../components/FaqAccordion';
import Footer from '../components/Footer';
import LoadingPage from '../components/LoadingPage';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination, Navigation } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';
import { landingPageService } from '../services/landingPageService';
import { CreatorLandingPage, CreatorLandingReview, CreatorLandingFaq, CreatorLandingFeature, Recipe } from '../types/database';
import { identifyCreator, trackButtonClick } from '../utils/mixpanel';
import { useNavigate, useLocation } from 'react-router-dom';

// Landing page ID for this specific page
const LANDING_PAGE_ID = '18da56f4-37c7-4e10-b5e0-a026c3e6d8a6'; // Change this value for each new landing page
const CREATOR_ID = '13';

// Fallback favicon path
const FALLBACK_FAVICON = '/favicon.png';

const Template1: React.FC = () => {
  console.log('Template1 component rendering'); // Basic debug log

  const [landingPageData, setLandingPageData] = useState<CreatorLandingPage | null>(null);
  const [reviews, setReviews] = useState<CreatorLandingReview[]>([]);
  const [faqs, setFaqs] = useState<CreatorLandingFaq[]>([]);
  const [features, setFeatures] = useState<CreatorLandingFeature[]>([]);
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    console.log('Template1 useEffect running'); // Debug log for useEffect
    const fetchData = async () => {
      console.log('Starting to fetch data for landing page ID:', LANDING_PAGE_ID);
      try {
        const [pageData, pageReviews, pageFaqs, pageFeatures, creatorRecipes] = await Promise.all([
          landingPageService.getLandingPageById(LANDING_PAGE_ID),
          landingPageService.getLandingPageReviews(LANDING_PAGE_ID),
          landingPageService.getLandingPageFaqs(LANDING_PAGE_ID),
          landingPageService.getLandingPageFeatures(LANDING_PAGE_ID),
          landingPageService.getCreatorRecipes(CREATOR_ID)
        ]);

        console.log('Fetched landing page data:', pageData); // Debug log for page data
        console.log('Accent color from database:', pageData?.accent_color); // Debug log for accent color
        console.log('Fetched reviews:', pageReviews); // Debug log for reviews
        console.log('Fetched FAQs:', pageFaqs); // Debug log for FAQs
        console.log('Fetched features:', pageFeatures); // Debug log for features
        console.log('Fetched recipes:', creatorRecipes); // Debug log for recipes

        // Check if we got any data at all
        if (!pageData) {
          console.error('No landing page data found for ID:', LANDING_PAGE_ID);
          setError('Landing page not found');
          setIsLoading(false);
          return;
        }

        setLandingPageData(pageData);
        setReviews(pageReviews);
        setFaqs(pageFaqs);
        setFeatures(pageFeatures);
        setRecipes(creatorRecipes);

        // Identify the creator in Mixpanel
        if (pageData) {
          identifyCreator(String(pageData.creator_id), pageData.name);
        }

        // Set the accent color if we have landing page data
        if (pageData?.accent_color) {
          // Ensure the color is properly formatted
          const accentColor = pageData.accent_color.startsWith('#') 
            ? pageData.accent_color 
            : `#${pageData.accent_color}`;
            
          console.log('Setting accent color to:', accentColor); // Debug log for setting accent color
          document.documentElement.style.setProperty('--accent-color', accentColor);
          console.log('Current accent color value:', getComputedStyle(document.documentElement).getPropertyValue('--accent-color')); // Debug log for verifying the set value
        }

        // Set the favicon
        const faviconLink = document.querySelector("link[rel~='icon']") as HTMLLinkElement;
        if (faviconLink) {
          faviconLink.href = pageData?.favicon || FALLBACK_FAVICON;
        }
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load page data');
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  // Debug effect to log when reviews and FAQs change
  useEffect(() => {
    console.log('Reviews updated, count:', reviews.length);
    console.log('FAQs updated, count:', faqs.length);
  }, [reviews, faqs]);

  // Add debug log for features state
  useEffect(() => {
    console.log('Current features state:', features);
  }, [features]);

  if (isLoading) {
    return <LoadingPage message="Loading creator page..." />;
  }

  if (error) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        fontFamily: 'Gabarito, sans-serif',
        color: 'red'
      }}>
        {error}
      </div>
    );
  }

  // Debug log before render
  console.log('Rendering with features:', features);
  console.log('Landing page data:', landingPageData);
  console.log('Is loading:', isLoading);
  console.log('Error:', error);

  // If we have no data but also no error, show a fallback
  if (!isLoading && !error && !landingPageData) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        fontFamily: 'Gabarito, sans-serif',
        flexDirection: 'column',
        gap: '20px'
      }}>
        <h1>Template 1 is working!</h1>
        <p>But no landing page data was found for ID: {LANDING_PAGE_ID}</p>
        <p>Creator ID: {CREATOR_ID}</p>
      </div>
    );
  }

  const renderStars = (count: number) =>
    Array.from({ length: count }, (_, i) => (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" key={i}>
        <path d="M3.825 19.5L5.45 12.475L0 7.75L7.2 7.125L10 0.5L12.8 7.125L20 7.75L14.55 12.475L16.175 19.5L10 15.775L3.825 19.5Z" fill="var(--accent-color)"/>
      </svg>
    ));

  return (
    <>
      <header
        style={{ backgroundImage: `url(${heroImage})` }}
      >
        <div className="top-menu">
          <img 
            src={landingPageData?.logo || logo} 
            alt="logo" 
            style={{ maxHeight: '40px', width: 'auto' }}
          />
          <a 
            href={`/sign-up/${CREATOR_ID}`}
            className="btn primary"
            onClick={(e) => {
              e.preventDefault();
              navigate(`/sign-up/${CREATOR_ID}`, { state: { from: location.pathname } });
              trackButtonClick('signup_header');
            }}
          >
            Sign up now
          </a>
        </div>
        <h1 className="titles large">{landingPageData?.payoff || 'Big on flavour, low on cost'}</h1>
        <div className='header-shade'>&nbsp;</div>
      </header>
      <section className="features">
        <div className='features-content'>
          {features.length > 0 ? (
            <Swiper
              modules={[Pagination]}
              pagination={{
                clickable: true,
                bulletClass: 'custom-swiper-bullet',
                bulletActiveClass: 'custom-swiper-bullet-active',
              }}
              slidesPerView={1}
              className="features-swiper"
            >
              {features.map((feature, index) => (
                <SwiperSlide key={feature.id} className="feature-slide">
                  <div className="column description">
                    <span className='paragraphs large color charcoal'>
                      {String(index + 1).padStart(2, '0')}
                    </span>
                    <p className='paragraphs large color black'>{feature.body}</p>
                    <a 
                      href={`/sign-up/${CREATOR_ID}`}
                      className="btn primary"
                      onClick={(e) => {
                        e.preventDefault();
                        navigate(`/sign-up/${CREATOR_ID}`, { state: { from: location.pathname } });
                        trackButtonClick('get_cooking_features');
                      }}
                    >
                      Get cooking now
                    </a>
                  </div>
                  <div className="column image">
                    <img src={feature.image || slide1} alt="features" />
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          ) : (
            <div>No features available</div>
          )}
        </div>
      </section>
      <section className="recipes-swiper">
        <div className='recipes-content'>
          <h2 className='titles small color black'>Access all my recipes</h2>
          <div style={{ width: '100%', position: 'relative' }}>
            <Swiper
              modules={[Pagination]}
              spaceBetween={30}
              pagination={{
                clickable: true,
                bulletClass: 'custom-swiper-bullet',
                bulletActiveClass: 'custom-swiper-bullet-active',
              }}
              slidesPerView={1.2}
              breakpoints={{
                600: { slidesPerView: 2 },
                900: { slidesPerView: 3 },
              }}
            >
              {recipes.map((recipe) => (
                <SwiperSlide key={recipe.id}>
                  <div className='recipe-card'>
                    {recipe.Free && <span className='is-free labels medium'>FREE RECIPE</span>}
                    <div className='recipe-card-image'>
                      <img src={recipe.ImageURL || recipe1} alt={recipe.Name} />
                    </div>
                    <div className='recipe-card-content'>
                      <span className='paragraphs large color dark-charcoal'>{recipe.Name}</span>
                      <span className='paragraphs small color charcoal'>{recipe.PrepTime + recipe.CookTime} min cook time</span>
                    </div>
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
          <a 
            href={`/sign-up/${CREATOR_ID}`}
            className='btn primary'
            onClick={(e) => {
              e.preventDefault();
              navigate(`/sign-up/${CREATOR_ID}`, { state: { from: location.pathname } });
            }}
          >
            Access all my recipes
          </a>
        </div>
      </section>
      {/* Sample Swiper Carousel Section */}
      <section className='reviews-carousel'>
        <h2 className="titles small color black">Used and loved by over 300,000 home cooks</h2>
        <div style={{ width: '100%', padding: '32px 0' }}>
          {reviews.length > 0 ? (
            <Swiper
              modules={[Pagination]}
              spaceBetween={30}
              pagination={{
                clickable: true,
                bulletClass: 'custom-swiper-bullet',
                bulletActiveClass: 'custom-swiper-bullet-active',
              }}
              slidesPerView={1.2}
              breakpoints={{
                600: { slidesPerView: 2 },
                900: { slidesPerView: 3 },
              }}
            >
              {reviews.map((review) => (
                <SwiperSlide key={review.id}>
                  <div className='review-card'>
                    <div className='review-rating'>
                      {renderStars(5)}
                    </div>
                    <p className='paragraphs medium color dark-charcoal'>{review.body}</p>
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          ) : (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <p className="paragraphs medium color charcoal">No reviews available</p>
            </div>
          )}
        </div>
      </section>
      <section className="cta" style={{ backgroundImage: `url(${landingPageData?.cta_image || ctaBg})` }}>
        <div className="cta-content">
          <h2 className="titles large color white">{landingPageData?.call_to_action || 'Are you ready to start cooking with me?'}</h2>
          <a 
            href={`/sign-up/${CREATOR_ID}`} 
            className="btn primary"
            onClick={() => trackButtonClick('join_community_cta')}
          >
            Join our community now
          </a>
        </div>
      </section>
      <section className='faqs'>
        <h2 className="titles small color black">Frequently Asked Questions</h2>
        {faqs.length > 0 ? (
          <FaqAccordion items={faqs.map(faq => ({
            question: faq.question,
            answer: faq.answer
          }))} />
        ) : (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <p className="paragraphs medium color charcoal">No FAQs available</p>
          </div>
        )}
      </section>
      <Footer creatorId={CREATOR_ID} />
    </>
  );
};

export default Template1; 